import React, { useState, useEffect } from "react";
import { Button, Typography, Form, Col, List, theme } from "antd";
import { InputObject } from "./default";
import DynamicForm from "./DynamicForm";
import { http } from "../Guard";
import _ from "lodash";
import { Icons } from "../assets/icons";
import Icon from "@ant-design/icons";
import { FormAction } from "./DynamicForm.default";
import { MAX_PAGE_SIZE } from "../repository/data.default";

export interface RelatedEntityManagerProps {
  readonly title: string;
  readonly addLabel?: string;
  readonly emptyText?: string;
  readonly getUrl?: string;
  readonly deleteUrl?: string;
  readonly addUrl?: string;
  readonly request: (search: string) => Promise<InputObject[]>;
  readonly displayField?: string;
  readonly onAdd?: (item: InputObject) => void;
  readonly onRemove?: (id: string) => void;
  readonly mode: FormAction;
  readonly parentUid?: string;
  readonly isSubmitted?: boolean;
  readonly setIsSubmitted: (isSubmitted: boolean) => void;
  readonly needsOrderToAdd?: boolean;
  readonly entityConfiguration?: {
    readonly listNameToDisplay?: string;
    readonly UidName?: string;
  };
}

/**
 * Component for managing related entities with add/remove functionality
 */
function RelatedEntityManager(props: RelatedEntityManagerProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const formValues = Form.useWatch<Record<string, InputObject>>([], form);
  const [entities, setEntities] = useState<InputObject[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function deleteEntity(entityUid: string, value: string) {
      if (props.deleteUrl) {
        const url = props.deleteUrl.replace("{value}", value);
        return http.delete(url.replace("{id}", entityUid));
      }
      return Promise.resolve();
    }

    async function addEntity(params: {
      entityUid: string;
      value: string;
      order?: number;
    }) {
      if (props.addUrl) {
        const url = props.addUrl.replace("{value}", params.value);
        const finalUrl = url.replace("{id}", params.entityUid);
        return http.post(finalUrl, params.order ? { order: params.order } : {});
      }
      return Promise.resolve();
    }

    async function addOrDeleteEntities(entityUid: string) {
      const addEntities = entities.filter(
        (entity) => entity.dataItem && entity.dataItem.__type === "add",
      );
      const deleteEntities = entities.filter(
        (entity) => entity.dataItem && entity.dataItem.__type === "delete",
      );

      const promises: any[] = [];

      if (deleteEntities.length) {
        deleteEntities.forEach(({ value }) => {
          promises.push(deleteEntity(entityUid, value));
        });
      }

      if (addEntities.length) {
        addEntities.forEach(({ dataItem }, index) => {
          promises.push(
            addEntity({
              entityUid: props.parentUid as string,
              value: dataItem.uid,
              order: props.needsOrderToAdd ? index + 1 : undefined,
            }),
          );
        });
      }

      return Promise.all(promises);
    }
    if (
      props.isSubmitted &&
      props.mode !== "view" &&
      props.parentUid &&
      !isLoading
    ) {
      (async () => {
        setIsLoading(true);
        await addOrDeleteEntities(props.parentUid as string);
        setIsLoading(false);
      })();
    }
  }, [
    entities,
    isLoading,
    props.addUrl,
    props.deleteUrl,
    props.isSubmitted,
    props.mode,
    props.needsOrderToAdd,
    props.parentUid,
  ]);

  useEffect(() => {
    async function getEntitiesForUid(uid: string) {
      if (props.getUrl) {
        const url = props.getUrl.replace("{id}", uid);
        return http
          .get(url, {
            params: {
              pageNumber: 0,
              pageSize: MAX_PAGE_SIZE,
              searchQuery: {
                conditions: [],
              },
            },
          })
          .then(({ data }) => data);
      }
      return Promise.resolve([]);
    }

    if (props?.parentUid && props.mode === "update") {
      (async () => {
        const result = await getEntitiesForUid(props.parentUid as string);
        const entities = result.data.map((item: any) => {
          return {
            display:
              item[props.entityConfiguration?.listNameToDisplay as string],
            value: item[props.entityConfiguration?.UidName as string], // todo: add prop in order to know
            dataItem: { ...item, __type: "update" },
          };
        });
        setEntities(entities);
      })();
    } else {
      form.resetFields();
      setEntities([]);
    }
  }, [
    form,
    props.getUrl,
    props.mode,
    props.parentUid,
    props.entityConfiguration?.listNameToDisplay,
    props.entityConfiguration?.UidName,
  ]);

  // const entityRequest = (search: string): Promise<InputObject[]> => {
  //   // If a custom request function is provided, use it
  //   if (props.request) {
  //     return props.request(search);
  //   }

  //   // If url is provided, use it to create a generic request
  //   if (props.url) {
  //     return retry(() =>
  //       http.get(props.url!, {
  //         params: {
  //           searchQuery: JSON.stringify({
  //             conditions: [
  //               {
  //                 // TODO: Update this in order to
  //                 field: props.displayField,
  //                 operation: QueryOperation.StartsWith,
  //                 values: [search],
  //               },
  //             ],
  //           }),
  //           pageNumber: 0,
  //           pageSize: 100,
  //         },
  //       }),
  //     ).then(({ data }) =>
  //       data.map((dataItem: { name: any; uid: any; }) => ({
  //         display: dataItem.name,
  //         value: dataItem.uid,
  //         dataItem,
  //       })),
  //     );
  //   }

  //   // If neither request nor url is provided, return empty array
  //   return Promise.resolve([]);
  // };

  function onAddEntity(): void {
    const entity = props?.title
      ? (formValues?.[props.title] as InputObject)
      : undefined;
    if (entity && entity.dataItem) {
      const updateEntities = _.uniqBy(
        [
          ...entities.filter(
            (item) => item.dataItem?.uid !== entity.dataItem?.uid,
          ),
          { ...entity, dataItem: { ...entity.dataItem, __type: "add" } },
        ],
        (item) => item.value,
      );
      setEntities(updateEntities);
      form.resetFields();
    }
  }

  function onRemoveEntity(uid: string): void {
    const filterEntities = entities.map((item, i) => {
      if (item.dataItem.uid === uid) {
        return { ...item, dataItem: { ...item.dataItem, __type: "delete" } };
      }

      return item;
    });
    setEntities(filterEntities);
  }

  return (
    <Col className="flex flex-1 flex-col">
      {/* Select field for adding items */}
      {props.mode !== "view" && (
        <DynamicForm
          className="w-full"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "combobox",
                    label: props.title,
                    name: props.title,
                    request: props.request,
                  },
                ],
              },
            ],
          }}
          onChange={onAddEntity}
        />
      )}
      {!!entities.filter(({ dataItem }) => dataItem?.__type !== "delete")
        .length && (
        <List
          className="ml-1 mr-1"
          style={{
            backgroundColor: token.colorBgLayout,
            borderRadius: token.borderRadius,
          }}
          dataSource={entities.filter(
            ({ dataItem }) => dataItem?.__type !== "delete",
          )}
          renderItem={(item, index) => {
            return (
              <List.Item className="p-1 pl-2 pr-2">
                <Col className="flex flex-auto flex-col">
                  <Typography.Text>{item.display}</Typography.Text>
                  <Typography.Text
                    style={{
                      color: token.colorTextDescription,
                    }}
                  >
                    {item.dataItem?.__name}
                  </Typography.Text>
                </Col>
                {props.mode !== "view" && (
                  <Button
                    type={"text"}
                    icon={
                      <Icon
                        className="-ml-0.5 text-xl"
                        component={Icons.CloseIcon}
                      />
                    }
                    onClick={() => onRemoveEntity(item.dataItem.uid)}
                  />
                )}
              </List.Item>
            );
          }}
        />
      )}
    </Col>
  );
}

export default RelatedEntityManager;
